<template>
  <div id="manual-add-bank" class="manual-add-bank">
    <div class="form-header">
      <div class="header-icon">
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M3 21H21M3 10H21M5 6L12 3L19 6M4 10V21M20 10V21M8 14V17M12 14V17M16 14V17" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </div>
      <div class="header-content">
        <h3 class="header-title">Enter Account Details</h3>
        <p class="header-description">Paid out in USD from your USD wallet, to a USD account in any country.</p>
      </div>
    </div>
      <form @submit.prevent="addBank" class="bank-form">
        <div class="form-field">
          <SelectInput
            label="Which country is this USD account in?"
            defaltOption="Select country"
            name="country"
            id="country"
            :showLabel="true"
            :list="countryList"
            @set="setCountry"
          />
        </div>
        <div class="form-field">
          <TextInput
            placeHolder="Account Name"
            :text="payload.account_name"
            type="text"
            name="accountName"
            id="accountName"
            label="Account Name"
            :showLabel="true"
            @set="setAccountName"
            @valid="accountNameValid"
          />
        </div>
        <div class="form-field">
          <TextInput
            placeHolder="Account Number"
            :text="payload.account_number"
            type="text"
            name="accountNumber"
            id="accountNumber"
            label="Account Number"
            :showLabel="true"
            @set="setAccountNumber"
            @valid="accountNumberValid"
          />
        </div>
        <div class="form-field">
          <TextInput
            placeHolder="Bank Name"
            :text="payload.bank_name"
            type="text"
            name="bankName"
            id="bankName"
            label="Bank Name"
            :showLabel="true"
            @set="setBankName"
            @valid="bankNameValid"
          />
        </div>
        <div class="form-field">
          <TextInput
            placeHolder="Swift Code"
            :text="payload.swift_code"
            :required="payload.currency !== 'NGN'"
            v-if="payload.currency !== 'NGN'"
            type="text"
            name="swiftCode"
            id="swiftCode"
            label="Swift Code"
            :showLabel="true"
            @set="setSwiftCode"
            @valid="swiftCodeValid"
          />
        </div>
        <div class="form-field">
          <TextInput
            placeHolder="Sort code or routing number"
            :text="payload.sort_code"
            type="text"
            name="sortCode"
            :required="payload.currency !== 'NGN'"
            v-if="payload.currency !== 'NGN'"
            id="sortCode"
            label="Sort Code / Routing Number"
            :showLabel="true"
            @set="setSortCode"
            @valid="sortCodeValid"
          />
        </div>
        <div class="form-field">
          <TextInput
            placeHolder="Bank Address"
            :text="payload.bank_address"
            type="text"
            name="address"
            id="address"
            label="Address"
            :showLabel="true"
            @set="setAddress"
            @valid="bankAddressValid"
          />
        </div>
        <Btn class="submit-btn" title="Add Bank" :loading="loading" />
    </form>
  </div>
</template>

<script>
import Btn from "@/components/general/BtnComponent.vue";
import SelectInput from "@/components/general/SelectInput.vue";
import countryRegionData from "@/utils/js/countryRegionData";
import TextInput from "@/components/general/TextInput";
import { mapActions } from "vuex";

export default {
  name: "ManualAddBank",

  emits: ["added"],

  components: {
    TextInput,
    SelectInput,
    Btn,
  },

  data() {
    return {
      loading: false,
      validInput: {
        account_name: false,
        account_number: false,
        bank_name: false,
        swift_code: false,
        sort_code: false,
        bank_address: false,
      },
      payload: {
        account_name: "",
        account_number: "",
        bank_name: "",
        currency: "USD",
        swift_code: "",
        sort_code: "",
        bank_address: "",
        country: undefined,
      },
      countryList: countryRegionData.map((c) => c.countryName),
    };
  },

  methods: {
    ...mapActions("Account", ["addBankAccount"]),
    ...mapActions(["showToast"]),

    addBank() {
      setTimeout(async () => {
        const baseValidation =
          this.validInput.account_name &&
          this.validInput.account_number &&
          this.validInput.bank_name &&
          this.validInput.bank_address;

        const codeValidation = this.isNGN(this.payload.currency)
          ? true
          : this.validInput.swift_code && this.validInput.sort_code;

        if (!baseValidation || !codeValidation) {
          return this.showToast({
            description: "Please fill all input field correctly",
            display: true,
            type: "error",
          });
        }

        const apiPayload = {
          ...this.payload,
          ...(this.isNGN(this.payload.currency) && {
            sort_code: "0000",
            swift_code: "0000",
          }),
        };

        if (!this.payload.country) {
          return this.showToast({
            description: "Please select the country this account is held in",
            display: true,
            type: "error",
          });
        }

        this.loading = true;
        try {
          const response = await this.addBankAccount(apiPayload);
          if (response) this.$emit("added", this.payload.currency);
        } catch (error) {
          console.error("Error adding bank:", error);
        } finally {
          this.loading = false;
        }
      }, 500);
    },

    isNGN(currency) {
      return currency === "NGN";
    },

    setAccountName(text) {
      this.payload.account_name = text;
    },

    setAccountNumber(text) {
      this.payload.account_number = text;
    },


    setCountry(option) {
      const country = countryRegionData.find((c) => c.countryName === option);
      this.payload.country = country?.countryShortCode;
    },

    setBankName(text) {
      this.payload.bank_name = text;
    },

    setSwiftCode(text) {
      this.payload.swift_code = text;
    },

    setSortCode(text) {
      this.payload.sort_code = text;
    },

    setAddress(text) {
      this.payload.bank_address = text;
    },

    accountNameValid(valid) {
      this.validInput.account_name = valid;
    },

    accountNumberValid(valid) {
      this.validInput.account_number = valid;
    },

    bankNameValid(valid) {
      this.validInput.bank_name = valid;
    },

    swiftCodeValid(valid) {
      this.validInput.swift_code = valid;
    },

    sortCodeValid(valid) {
      this.validInput.sort_code = valid;
    },

    bankAddressValid(valid) {
      this.validInput.bank_address = valid;
    },
  },
};
</script>

<style lang="scss" scoped>
.manual-add-bank {
  padding: 8px 0;
}

.form-header {
  display: flex;
  gap: 16px;
  padding: 16px;
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 10px;
  margin-bottom: 24px;
}

.header-icon {
  width: 44px;
  height: 44px;
  min-width: 44px;
  background: rgba(77, 132, 255, 0.1);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 22px;
    height: 22px;
    color: #4D84FF;
  }
}

.header-content {
  flex: 1;
}

.header-title {
  font-size: 16px;
  font-weight: 600;
  color: #0F172A;
  margin: 0 0 4px 0;
}

.header-description {
  font-size: 13px;
  color: #64748B;
  margin: 0;
  line-height: 1.4;
}

.bank-form {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.form-field {
  margin-bottom: 16px;
}

.submit-btn {
  margin-top: 24px;
}
</style>
